import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '../components/LoginForm';
import { useReduxLoginForm } from '../hooks/useReduxLoginForm';
import { useAuthCheck } from '../hooks/useAuthCheck';
import { clearAuthChallenge } from '../../../redux/actions/authActions';
import { CHALLENGE_NAME } from '../../../utils/constants';
import crcEvansLogo from '../../../assets/images/crc-evans-logo.png';

/**
 * LoginPage Component
 *
 * Handles user authentication through a login form using Redux Saga.
 * Uses AWS Cognito for authentication.
 * Includes OTP verification flow before login.
 * Automatically redirects to appropriate page if user is already authenticated.
 * Handles NEW_PASSWORD_REQUIRED challenge by redirecting to reset password page.
 */
const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAuthenticated, isLoading: authCheckLoading } = useAuthCheck();

  const {
    formData,
    isLoading,
    error,
    handleInputChange,
    handleSubmit,
    otpSent,
    otp,
    setOtp,
    otpVerified,
    isVerifying,
    timer,
    canResend,
    handleResendOtp,
    handleVerifyOtp,
    handleCancelOtp,
    resendMessage,
  } = useReduxLoginForm();

  const authChallenge = useSelector((state: any) => state.auth.authChallenge);
  const challengeUser = useSelector((state: any) => state.auth.challengeUser);
  const userPermissions = useSelector((state: any) => state.userManagement.userPermissions);

  useEffect(() => {
    if (authChallenge !== '') {
      if (authChallenge === CHALLENGE_NAME) {
        navigate('/reset-password', {
          state: {
            email: challengeUser ? challengeUser.username : '',
            isRedirectedFromLogin: true,
          },
        });
        dispatch(clearAuthChallenge());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChallenge]);

  const loadingShell = (message: string) => (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page-bg py-12 px-4 font-sans antialiased">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hero-red mx-auto" />
        <p className="mt-4 text-heritage-grey">{message}</p>
      </div>
    </div>
  );

  if (authCheckLoading && !isAuthenticated) {
    return loadingShell('Checking authentication...');
  }

  if (isAuthenticated && userPermissions.data === null) {
    return loadingShell('Checking Permissions...');
  }

  if (isAuthenticated) {
    return loadingShell('Redirecting...');
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page-bg py-12 px-4 font-sans antialiased">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-sm p-8 space-y-6 sm:px-9">
          <header className="text-center space-y-4">
            <img
              src={crcEvansLogo}
              alt="CRC Evans"
              className="h-9 w-auto mx-auto object-contain"
            />
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">
                <span className="text-hero-red">DATA</span>{' '}
                <span className="text-heritage-grey">360</span>
              </h1>
              <p className="mt-1 text-sm text-heritage-grey">
                {otpSent ? 'Security Verification' : 'Sign in to your account'}
              </p>
            </div>
          </header>

          <LoginForm
            formData={formData}
            isLoading={isLoading}
            error={error}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            otpSent={otpSent}
            otp={otp}
            setOtp={setOtp}
            otpVerified={otpVerified}
            isVerifying={isVerifying}
            timer={timer}
            canResend={canResend}
            onResendOtp={handleResendOtp}
            onVerifyOtp={handleVerifyOtp}
            onCancelOtp={handleCancelOtp}
            resendMessage={resendMessage}
          />
        </div>
      </div>

      <div className="mt-7 text-center text-[12px] text-heritage-grey">
        © {new Date().getFullYear()} Data 360. All rights reserved.
      </div>
    </div>
  );
};

export default LoginPage;
